/**
 * Schedule Quotas service — access to `scheduleQuotas`.
 *
 * One doc per course bucket (doc id = bucketKey: the class's seriesId, else
 * `pair_{studentId}_{mentorId}`) holding the cancellation counters and the
 * mentor's rolling reschedule window. Written by the scheduling Cloud
 * Functions — plus ONE admin-only write here: recording an admin-performed
 * reschedule against the mentor's rolling window (rules allow admin writes).
 * See src/features/scheduling/policy.js for the derivation helpers
 * (effectiveLimits, mentorWindowStanding).
 */
import firestoreService, { where, serverTimestamp, Timestamp } from './firestore.service';

const COLLECTION = 'scheduleQuotas';
const DAY_MS = 24 * 60 * 60 * 1000;

export const scheduleQuotasService = {
  /** Realtime subscription to one bucket's quota doc (null until it exists). */
  subscribe(bucketKey, onData, onError) {
    return firestoreService.subscribeDocument(COLLECTION, bucketKey, onData, onError);
  },

  get(bucketKey) {
    return firestoreService.getDocument(COLLECTION, bucketKey);
  },

  /** All quota buckets involving one mentor (per-student allowance table). */
  subscribeByMentor(mentorId, onData, onError) {
    return firestoreService.subscribeCollection(
      COLLECTION,
      [where('mentorId', '==', mentorId)],
      onData,
      onError
    );
  },

  /** All quota buckets involving one student. */
  subscribeByStudent(studentId, onData, onError) {
    return firestoreService.subscribeCollection(
      COLLECTION,
      [where('studentId', '==', studentId)],
      onData,
      onError
    );
  },

  /** Admin: every quota bucket platform-wide. */
  subscribeAll(onData, onError) {
    return firestoreService.subscribeCollection(COLLECTION, [], onData, onError);
  },

  /**
   * ADMIN ONLY — consume one mentor reschedule from the bucket's anchored
   * rolling window, exactly like the server's `consumeMentorReschedule`
   * (functions/src/scheduling/quota.js — keep the math in sync). Used when an
   * admin reschedules a class "on behalf of the mentor" so the mentor's quota
   * meters reflect it. Creates the bucket doc if none exists yet.
   *
   * @param {object} classItem  { seriesId?, studentId, mentorId }
   * @param {object} policy     the scheduling policy (for window length)
   * @param {object} [actor]    { actorId, actorName } for the audit trail
   */
  async adminConsumeMentorReschedule(classItem, policy, actor = {}) {
    const bucketKey = classItem.seriesId || `pair_${classItem.studentId || 'none'}_${classItem.mentorId || 'none'}`;
    const existing = await firestoreService.getDocument(COLLECTION, bucketKey);

    const nowMs = Date.now();
    const nowTs = Timestamp.fromMillis(nowMs);
    const windowMs = (policy?.mentorRescheduleWindowDays || 30) * DAY_MS;

    const startMs = existing?.mentorRescheduleWindowStartAt?.toMillis?.() ?? null;
    const windowRunning = startMs != null && nowMs - startMs < windowMs;
    const events = (existing?.mentorRescheduleEvents || []).filter(
      (t) => (t?.toMillis?.() ?? 0) >= nowMs - windowMs
    );
    events.push(nowTs);

    const auditEntry = {
      at: nowTs,
      action: 'admin_reschedule_recorded',
      summary: "Reschedule recorded against the mentor's allowance by an administrator.",
      actorId: actor.actorId || '',
      actorName: actor.actorName || '',
      actorRole: 'admin',
    };

    const patch = {
      mentorRescheduleWindowStartAt: windowRunning ? existing.mentorRescheduleWindowStartAt : nowTs,
      mentorRescheduleWindowCount: windowRunning ? (existing.mentorRescheduleWindowCount || 0) + 1 : 1,
      mentorRescheduleEvents: events,
      auditTrail: [...(existing?.auditTrail || []), auditEntry],
      updatedAt: serverTimestamp(),
    };

    if (existing) {
      return firestoreService.updateDocument(COLLECTION, bucketKey, patch);
    }
    return firestoreService.setDocument(COLLECTION, bucketKey, {
      bucketKey,
      seriesId: classItem.seriesId || null,
      studentId: classItem.studentId || '',
      mentorId: classItem.mentorId || '',
      studentCancellationsUsed: 0,
      mentorCancellationsUsed: 0,
      overrides: {
        studentCancelLimit: null,
        mentorCancelLimit: null,
        mentorRescheduleLimit: null,
      },
      createdAt: serverTimestamp(),
      ...patch,
    });
  },
};

export default scheduleQuotasService;
